/**
 * @-mention detection and insertion for the chat composer (components/ChatThread.js).
 *
 * The composer used to look at the LAST "@" in the whole message and at everything
 * after it to the end. An "@" typed into the middle of a message already written never
 * offered names, because the rest of the message (its commas, digits, apostrophes) was
 * read as the name being typed. And picking a name replaced everything after the last
 * "@" with that name, deleting the rest of the message. Both now work from the CARET.
 *
 * Pure and dependency-free so node can test it: `node src/lib/mentions.test.mjs`.
 */

// What a name being typed may contain: letters in any script, combining marks, spaces,
// apostrophes, periods and hyphens. Anything else (a comma, a digit, a line break) ends
// the name, so the text at the caret is no longer a mention.
const NAME_CHARS = /^[\p{L}\p{M} '’.-]*$/u;

// An "@" starts a mention only at the start of the text or after whitespace, an opening
// bracket or a quote. "ana@studio.com" is an email address, not a tag.
const MENTION_BOUNDARY = /[\s([{"'“‘]/u;

// Past this many characters after the "@" it is a sentence, not a name being typed.
export const MAX_QUERY_LEN = 40;

/**
 * The mention being typed at `caret`, or null.
 * Returns { start, query }: `start` is the index of the "@" and `query` is the text
 * between it and the caret.
 */
export function mentionQueryAt(text, caret) {
  if (typeof text !== 'string' || !Number.isInteger(caret) || caret < 1 || caret > text.length) return null;
  const at = text.lastIndexOf('@', caret - 1);
  if (at === -1 || caret - at - 1 > MAX_QUERY_LEN) return null;
  if (at > 0 && !MENTION_BOUNDARY.test(text[at - 1])) return null;
  const query = text.slice(at + 1, caret);
  if (!NAME_CHARS.test(query)) return null;
  return { start: at, query };
}

// After the inserted name no space is added when the message already continues with
// whitespace, or with punctuation that belongs right after a name ("@Zach, can you").
const CONTINUES_CLEANLY = /^[\s.,!?;:)\]}]/u;

/**
 * Replace the mention being typed (from `start` up to `caret`) with "@{name}", keep
 * everything after the caret, and return { text, caret }. The caret lands after the
 * name and the space that follows it, or right after the name when punctuation follows.
 */
export function insertMentionAt(text, start, caret, name) {
  const before = text.slice(0, start);
  const after = text.slice(caret);
  const token = `@${name}`;
  if (CONTINUES_CLEANLY.test(after)) {
    const skip = /^\s/u.test(after) ? 1 : 0;
    return { text: before + token + after, caret: before.length + token.length + skip };
  }
  return { text: `${before}${token} ${after}`, caret: before.length + token.length + 1 };
}
